import * as Sentry from '@sentry/react';
import {useLDClient} from 'launchdarkly-react-client-sdk';
import {useEffect, useRef, useSyncExternalStore} from 'react';

import {buildLaunchDarklyContext} from '~utils/launchDarklyContext';
import {shouldMountLaunchDarkly} from '~utils/launchDarklyEnv';
import {selectCustomer, selectUserEmail, selectUserId, selectUsername} from '~redux/reducers/userReducer';
import {useAppSelector} from '~redux/index';

// When LaunchDarkly is not mounted (local dev, tests) flags come from the static dev defaults, so
// they are ready immediately and must never block rendering.
let areFlagsReady = !shouldMountLaunchDarkly;
const readyListeners = new Set<() => void>();

/** Attempts per identity, including the first one. With the delay below this spans ~30 seconds. */
const MAX_IDENTIFY_ATTEMPTS = 5;
const IDENTIFY_RETRY_BASE_DELAY_MS = 2000;

function subscribeToFlagsReady(onStoreChange: () => void): () => void {
  readyListeners.add(onStoreChange);
  return () => {
    readyListeners.delete(onStoreChange);
  };
}

/**
 * Marks flags as evaluated against the identified user — or as good as they will get, once the
 * identification has been retried `MAX_IDENTIFY_ATTEMPTS` times without success. Exported for tests.
 */
export function markFeatureFlagsReady(): void {
  if (areFlagsReady) {
    return;
  }
  areFlagsReady = true;
  readyListeners.forEach((listener) => listener());
}

/**
 * Whether LaunchDarkly has evaluated the flags for the current user.
 *
 * Until it has, every flag reads `undefined` (falsy), which would hide a feature from exactly the
 * users it was enabled for. Use this at the entry point of a flag-gated feature — especially one
 * behind a route guard that redirects on a falsy flag — to render a loading state instead of the
 * disabled state. Existing flags that only hide a menu entry do not need it.
 *
 * Note that this also turns `true` once the identification has failed `MAX_IDENTIFY_ATTEMPTS` times:
 * the flags then keep the values of the pre-identify (anonymous) context, which is a better outcome
 * than a loading state that never resolves.
 */
export function useAreFeatureFlagsReady(): boolean {
  return useSyncExternalStore(
    subscribeToFlagsReady,
    () => areFlagsReady,
    () => true,
  );
}

/**
 * Keeps the LaunchDarkly context in sync with the logged-in user, so flags can be targeted at the
 * user (individually or via a segment such as internal `@*.maddox.ai` accounts) as well as at the
 * customer organization.
 *
 * No environment guard is needed: without the provider `useLDClient()` returns `undefined` and this
 * hook is inert.
 */
export function useLaunchDarklyIdentify(): void {
  const ldClient = useLDClient();
  const userId = useAppSelector(selectUserId);
  const email = useAppSelector(selectUserEmail);
  const name = useAppSelector(selectUsername);
  const customer = useAppSelector(selectCustomer);
  const lastIdentifiedContextRef = useRef<string>();

  useEffect(() => {
    if (!ldClient) {
      return;
    }

    // `userId` is only set once the user data has been fetched, which requires an authenticated
    // session, so it doubles as the logged-in check.
    const context = buildLaunchDarklyContext({userId, email, name, customer});
    if (!context) {
      return;
    }

    const serializedContext = JSON.stringify(context);
    if (lastIdentifiedContextRef.current === serializedContext) {
      return;
    }
    // Recorded up front so the StrictMode double-effect does not fire a second identify call while
    // the first one is still in flight. The cleanup below drops it again when this run is abandoned.
    lastIdentifiedContextRef.current = serializedContext;

    let isSuperseded = false;
    let retryTimeout: ReturnType<typeof setTimeout> | undefined;

    const identifyWithRetry = (attempt: number): void => {
      ldClient.identify(context, undefined, (error) => {
        // A newer identity (or a StrictMode remount) took over while this call was in flight.
        if (isSuperseded) {
          return;
        }

        if (!error) {
          markFeatureFlagsReady();
          return;
        }

        Sentry.captureException(error);

        if (attempt < MAX_IDENTIFY_ATTEMPTS) {
          // The identity fields are unchanged, so nothing reruns this effect on its own: a transient
          // failure only heals if the next attempt is scheduled here.
          retryTimeout = setTimeout(
            () => identifyWithRetry(attempt + 1),
            IDENTIFY_RETRY_BASE_DELAY_MS * 2 ** (attempt - 1),
          );
          return;
        }

        // Out of attempts. Drop the guard so a later identity change starts over, and stop blocking
        // the readiness gate: the flags keep the previous (anonymous) context's values, but leaving
        // gated features on a loading state for the whole session is worse than acting on those.
        lastIdentifiedContextRef.current = undefined;
        markFeatureFlagsReady();
      });
    };

    identifyWithRetry(1);

    return () => {
      isSuperseded = true;
      clearTimeout(retryTimeout);
      // This run no longer owns the identification: either the identity changed — the next run
      // identifies the new one — or React is remounting under StrictMode. Either way the guard must
      // not outlive the abandoned attempt, or nothing would ever identify this context again.
      lastIdentifiedContextRef.current = undefined;
    };
  }, [ldClient, userId, email, name, customer]);
}
